import { createCopilotRequestGate } from '@/composables/copilot/requestGate';
import {
    claimToast,
    index,
    show,
} from '@/routes/ai/transactions/reports/generations';
import { useNotificationStore } from '@/stores/notificationStore';
import { useToastStore } from '@/stores/toastStore';
import {
    isActiveGeneration,
    type AiReportGeneration,
} from '@/types/ai-report-generation';
import { http, HttpResponseError } from '@inertiajs/core';
import { router } from '@inertiajs/vue3';
import { trans } from 'laravel-vue-i18n';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

interface GenerationPage {
    data: AiReportGeneration[];
    links: { next: string | null };
}
export const useAiReportGenerationStore = defineStore(
    'ai-report-generations',
    () => {
        const generations = ref<AiReportGeneration[]>([]);
        const unavailable = ref(false);
        const context = ref('');
        const historyHasMore = ref(false);
        const historyLoading = ref(false);
        let historyNextPage = 1;
        const activeCount = computed(
            () => generations.value.filter(isActiveGeneration).length,
        );
        const gate = createCopilotRequestGate();
        const toasts = useToastStore();
        let timer: ReturnType<typeof setTimeout> | undefined;
        let controller: AbortController | undefined;
        let inFlight = false;
        let needsReconcile = false;
        const pendingTerminalChecks = new Set<string>();
        let backoff = 3000;
        let nextPage = 1;
        const seenAcrossPages = new Set<string>();
        let version = 0;
        const observations = new Map<string, number>();
        const acceptanceEpochs = new Map<string, number>();
        const localTerminalClaims = new Map<string, string>();
        const pendingResumes = new Map<string, string>();
        let signal: (() => void) | undefined;
        let currentComponent = '';
        const prefix = () => `ai-generation:${context.value}:`;
        const paused = () =>
            typeof document === 'undefined' ||
            typeof navigator === 'undefined' ||
            document.hidden ||
            !navigator.onLine;
        function clear() {
            clearTimeout(timer);
            controller?.abort();
            gate.invalidate();
            version++;
            inFlight = false;
            needsReconcile = false;
            pendingTerminalChecks.clear();
            toasts.clearPrefix(prefix());
            useNotificationStore().invalidateContext();
            generations.value = [];
            observations.clear();
            acceptanceEpochs.clear();
            localTerminalClaims.clear();
            pendingResumes.clear();
            unavailable.value = false;
            nextPage = 1;
            seenAcrossPages.clear();
            historyNextPage = 1;
            historyHasMore.value = false;
            historyLoading.value = false;
        }
        function setContext(
            user: string | undefined,
            household: string | undefined,
        ) {
            const next = user && household ? `${user}:${household}` : '';
            if (next === context.value) return;
            clear();
            context.value = next;
            backoff = 3000;
            if (next) void reconcile();
        }
        function schedule(delay = backoff) {
            clearTimeout(timer);
            if (context.value && !paused())
                timer = setTimeout(() => void reconcile(), delay);
        }
        function observe(id: string) {
            observations.set(id, (observations.get(id) ?? 0) + 1);
        }
        function unchangedSince(snapshot: Map<string, number>, id: string) {
            return observations.get(id) === snapshot.get(id);
        }
        function remove(id: string) {
            observe(id);
            generations.value = generations.value.filter(
                (item) => item.id !== id,
            );
            localTerminalClaims.delete(id);
            pendingResumes.delete(id);
            pendingTerminalChecks.delete(id);
            toasts.dismiss(prefix() + id, false);
        }
        function terminalIdentity(item: AiReportGeneration) {
            return JSON.stringify([
                item.status,
                item.completed_at,
                item.report_id,
                item.error_code,
            ]);
        }
        function accept(item: AiReportGeneration, announce = false) {
            if (item.household_id !== context.value.split(':')[1]) return;
            observe(item.id);
            const previous = generations.value.findIndex(
                (generation) => generation.id === item.id,
            );
            if (
                previous >= 0 &&
                isActiveGeneration(generations.value[previous]) &&
                !isActiveGeneration(item)
            )
                pendingTerminalChecks.add(item.id);
            if (previous < 0) generations.value.unshift(item);
            else generations.value[previous] = item;
            if (isActiveGeneration(item)) {
                pendingTerminalChecks.delete(item.id);
                // A fresh active observation starts a new cycle, including safe resume of this UUID.
                if (localTerminalClaims.delete(item.id))
                    toasts.dismiss(prefix() + item.id, false);
                if (announce)
                    toasts.upsert({
                        id: prefix() + item.id,
                        type: 'info',
                        message: trans('ai.generation_waiting'),
                        loading: true,
                        timeout: null,
                    });
            }
        }
        function beginResume(id: string) {
            const item = generations.value.find((value) => value.id === id);
            if (!item?.can_retry || item.retry_kind !== 'resume') return;
            pendingResumes.set(id, terminalIdentity(item));
            observe(id);
            acceptanceEpochs.set(id, (acceptanceEpochs.get(id) ?? 0) + 1);
        }
        function cancelResume(id: string) {
            pendingResumes.delete(id);
        }
        function accepted(item: AiReportGeneration) {
            if (item.household_id !== context.value.split(':')[1]) return;
            const previous = generations.value.find(
                (value) => value.id === item.id,
            );
            const resumeOf = pendingResumes.get(item.id);
            pendingResumes.delete(item.id);
            // A delayed initial/replayed acceptance cannot reopen an observed terminal cycle.
            // Only the explicit resume of that still-current failed cycle may do so.
            if (
                previous &&
                !isActiveGeneration(previous) &&
                resumeOf !== terminalIdentity(previous)
            )
                return;
            acceptanceEpochs.set(
                item.id,
                (acceptanceEpochs.get(item.id) ?? 0) + 1,
            );
            // Acceptance acknowledges the request; a live observation may already know its later phase.
            accept(
                previous &&
                    isActiveGeneration(previous) &&
                    isActiveGeneration(item)
                    ? previous
                    : item,
                true,
            );
            signal?.();
            schedule(0);
        }
        async function json<T>(
            method: 'get' | 'post',
            url: string,
            abortSignal: AbortSignal,
        ): Promise<T> {
            const response = await http.getClient().request({
                method,
                url,
                signal: abortSignal,
                headers: {
                    Accept: 'application/json',
                    'X-Finzzo-Background': 'true',
                },
            });
            return JSON.parse(response.data) as T;
        }
        async function reconcile() {
            if (!context.value || paused()) return;
            if (inFlight) {
                // A timer/focus wake during a slow request must survive its completion.
                needsReconcile = true;
                return;
            }
            needsReconcile = false;
            clearTimeout(timer);
            inFlight = true;
            const ticket = gate.begin();
            const cycle = version;
            const beforeRead = new Map(observations);
            const intents = new Map(acceptanceEpochs);
            const currentIntent = (id: string) =>
                (acceptanceEpochs.get(id) ?? 0) === (intents.get(id) ?? 0);
            const currentRead = (id: string) =>
                currentIntent(id) && unchangedSince(beforeRead, id);
            const currentTerminal = (item: AiReportGeneration) => {
                const current = generations.value.find(
                    (value) => value.id === item.id,
                );
                return (
                    currentIntent(item.id) &&
                    !!current &&
                    !isActiveGeneration(current) &&
                    terminalIdentity(current) === terminalIdentity(item)
                );
            };
            controller = new AbortController();
            let more = false;
            try {
                // Read a bounded batch before claiming: offset pagination remains stable during this read.
                const items: AiReportGeneration[] = [];
                if (nextPage === 1) seenAcrossPages.clear();
                for (let count = 0; count < 5; count++) {
                    const page = await json<GenerationPage>(
                        'get',
                        index({ query: { tracking: 1, page: nextPage } }).url,
                        controller.signal,
                    );
                    if (!gate.accepts(ticket)) return;
                    items.push(...page.data);
                    page.data.forEach((item) => seenAcrossPages.add(item.id));
                    more = !!page.links.next;
                    nextPage = more ? nextPage + 1 : 1;
                    if (!more) break;
                }
                // Another tab may have already claimed a known active request's terminal toast.
                for (const old of (more
                    ? []
                    : generations.value
                          .filter(
                              (old) =>
                                  isActiveGeneration(old) ||
                                  pendingTerminalChecks.has(old.id),
                          )
                          .filter((old) => !seenAcrossPages.has(old.id))
                          .sort(
                              (a, b) =>
                                  Number(pendingTerminalChecks.has(b.id)) -
                                  Number(pendingTerminalChecks.has(a.id)),
                          )
                ).slice(0, 20)) {
                    try {
                        const result = await json<{ data: AiReportGeneration }>(
                            'get',
                            show(old.id).url,
                            controller.signal,
                        );
                        if (!gate.accepts(ticket)) return;
                        items.push(result.data);
                    } catch (error) {
                        if (!gate.accepts(ticket)) return;
                        if (
                            error instanceof HttpResponseError &&
                            error.response.status === 404
                        ) {
                            if (currentRead(old.id)) remove(old.id);
                        } else throw error;
                    }
                }
                let terminalSeen = false;
                for (const item of new Map(
                    items.map((item) => [item.id, item]),
                ).values()) {
                    if (!gate.accepts(ticket)) return;
                    if (item.household_id !== context.value.split(':')[1]) {
                        clear();
                        context.value = '';
                        return;
                    }
                    // Every newer observation (history, acceptance or resume) supersedes this read.
                    if (!currentRead(item.id)) continue;
                    accept(item);
                    if (isActiveGeneration(item)) continue;
                    let result: { claimed: boolean };
                    try {
                        result = await json<{ claimed: boolean }>(
                            'post',
                            claimToast(item.id).url,
                            controller.signal,
                        );
                    } catch (error) {
                        if (!gate.accepts(ticket)) return;
                        if (
                            error instanceof HttpResponseError &&
                            error.response.status === 404
                        ) {
                            if (currentTerminal(item)) remove(item.id);
                            continue;
                        }
                        throw error;
                    }
                    if (!gate.accepts(ticket)) return;
                    // Claims may finish after another reader: deliver only for the same terminal cycle.
                    if (!currentTerminal(item)) continue;
                    if (result.claimed) {
                        localTerminalClaims.set(
                            item.id,
                            terminalIdentity(item),
                        );
                        toasts.upsert(
                            {
                                id: prefix() + item.id,
                                type:
                                    item.status === 'completed'
                                        ? 'success'
                                        : 'error',
                                message: trans(
                                    item.status === 'completed'
                                        ? 'ai.generation_completed'
                                        : generationErrorKey(item.error_code),
                                ),
                                timeout: null,
                                action: {
                                    label: trans(
                                        item.report_url
                                            ? 'ai.view_report'
                                            : 'ai.generation_follow',
                                    ),
                                    href: item.report_url ?? indexReportsUrl(),
                                },
                            },
                            true,
                        );
                    } else if (
                        localTerminalClaims.get(item.id) !==
                        terminalIdentity(item)
                    ) {
                        // A different tab's claim hides waiting, but cannot erase this tab's own terminal toast.
                        toasts.dismiss(prefix() + item.id, false);
                    }
                    pendingTerminalChecks.delete(item.id);
                    terminalSeen = true;
                }
                if (terminalSeen) {
                    useNotificationStore().lastFetchedAt = null;
                    void useNotificationStore().fetchUnreadNotifications(true);
                    refreshVisibleReports();
                }
                // Claims shift offset pages; restart from page one to drain every terminal entry.
                if (items.some((item) => !isActiveGeneration(item)))
                    nextPage = 1;
                unavailable.value = false;
                backoff = 3000;
                if (
                    activeCount.value ||
                    pendingTerminalChecks.size ||
                    more ||
                    items.some((item) => !isActiveGeneration(item))
                )
                    schedule();
            } catch (error) {
                if (!gate.accepts(ticket)) return;
                const status =
                    error instanceof HttpResponseError
                        ? error.response.status
                        : undefined;
                if (status === 401 || status === 403) {
                    clear();
                    context.value = '';
                    return;
                }
                unavailable.value = true;
                backoff = Math.min(30000, backoff * 2);
                schedule();
            } finally {
                if (cycle === version) {
                    inFlight = false;
                    if (needsReconcile) {
                        needsReconcile = false;
                        schedule(0);
                    }
                }
            }
        }
        async function loadHistory(more = false) {
            const owner = context.value;
            const cycle = version;
            if (
                !owner ||
                typeof document === 'undefined' ||
                historyLoading.value
            )
                return;
            historyLoading.value = true;
            const beforeRead = new Map(observations);
            try {
                const requestPage = more ? historyNextPage : 1;
                const page = await json<GenerationPage>(
                    'get',
                    index({ query: { page: requestPage } }).url,
                    new AbortController().signal,
                );
                if (context.value !== owner || cycle !== version) return;
                page.data.forEach((item) => {
                    // History is a snapshot: live observations or resume intent made while it was pending win.
                    if (unchangedSince(beforeRead, item.id)) accept(item);
                });
                historyHasMore.value = !!page.links.next;
                historyNextPage = requestPage + 1;
                if (activeCount.value || pendingTerminalChecks.size) schedule();
            } catch {
                /* Tracking retries independently; history can be refreshed by revisiting this page. */
            } finally {
                if (cycle === version) historyLoading.value = false;
            }
        }
        function refreshVisibleReports() {
            // Inertia reload preserves scroll and page state itself.
            if (currentComponent === 'Ai/Reports/Index')
                router.reload({
                    only: ['reports'],
                    reset: ['reports'],
                    data: { page: 1 },
                });
            if (currentComponent === 'Dashboard/Index')
                router.reload({
                    only: ['latestReports'],
                });
            if (currentComponent === 'Ai/Reports/Show')
                router.reload({ only: ['report'] });
        }
        function setComponent(component: string) {
            currentComponent = component;
        }
        function connectSignal(callback: () => void) {
            signal = callback;
        }
        return {
            generations,
            unavailable,
            activeCount,
            context,
            clear,
            setContext,
            accepted,
            beginResume,
            cancelResume,
            reconcile,
            setComponent,
            connectSignal,
            loadHistory,
            historyHasMore,
            historyLoading,
        };
    },
);

import { index as reportsIndex } from '@/routes/ai/transactions/reports';
const indexReportsUrl = () => reportsIndex().url;
export function generationErrorKey(code: string | null): string {
    const known = [
        'queue_unavailable',
        'no_expenses',
        'no_key',
        'quota_exceeded',
        'source_changed',
        'configuration_changed',
        'filter_unavailable',
        'provider_error',
        'invalid_response',
        'uncertain',
        'unauthorized',
    ];
    return known.includes(code ?? '')
        ? `ai.report_generation.errors.${code}`
        : 'ai.generation_failed';
}
